import { useAuth } from '@/context/AuthContext';
import { api } from '@/lib/trpc/react';
import { DEFAULT_AI_MODELS } from '@unriddle/common';
import { useAtom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';
import { useEffect } from 'react';

const returnPageAtom = atomWithStorage<string>('return-page', '/');

export type ResponseTypeOption = {
  label: string;
  value: number;
  tier?: string;
  adminOnly?: boolean;
};

export const allModels = [
  {
    label: 'Claude 4 Sonnet',
    name: 'us.anthropic.claude-sonnet-4-20250514-v1:0',
    tier: 'Pro',
    description: 'Best for writing and exploring ideas',
  },
  // {
  //   label: 'Deepseek R1',
  //   name: 'deepseek-r1-distill-llama-70b',
  //   tier: 'Pro',
  //   description: 'Advanced reasoning for complex tasks',
  // },
  {
    label: 'GPT 4.1',
    name: 'gpt-4.1',
    tier: 'Pro',
    description: 'Balances speed and intelligence',
  },
  {
    label: 'o4-mini',
    name: 'o4-mini',
    tier: 'Pro',
    description: "OpenAI's latest thinking model",
    adminOnly: true,
  },
  {
    label: 'Gemini 2.5 Pro',
    name: 'gemini-2.5-pro',
    tier: 'Pro',
    description: 'Best for large amounts of files',
  },
  {
    label: 'Gemini 2.5 Flash',
    name: 'gemini-2.5-flash-lite-preview-06-17',
    description: 'Fastest model for simple tasks',
  },
];

export const models = allModels.filter((model) => !model.adminOnly);

export const responseTypeOptions: ResponseTypeOption[] = [
  { label: '50 words', value: 50 },
  { label: '120 words', value: 120 },
  { label: '250 words', value: 250 },
  { label: '500 words', value: 500, tier: 'Pro' },
  { label: 'No limit', value: 9999, tier: 'Pro' },
];

export const defaultSettings = {
  modelName: DEFAULT_AI_MODELS[0],
  temperature: 0.2,
  maxLength: 250,
  language: 'English (US)',
  artifactsEnabled: true,
  autoRenameEnabled: true,
  editorModelName: DEFAULT_AI_MODELS[0],
  tabAutocomplete: true, // TODO: to be used when we want the default to be true..
  tabAutocompleteEnabled: false,
};

export const useSettings = () => {
  const utils = api.useUtils();
  const { user, userPlanDetails } = useAuth();

  const [returnPage, setReturnPage] = useAtom(returnPageAtom);

  const { data: settings, isLoading } = api.userSettings.get.useQuery(
    undefined,
    {
      enabled: !!user,
    },
  );

  const { mutate } = api.userSettings.update.useMutation();

  const updateSettings = (newSettings: {
    modelName: string;
    temperature: number;
    maxLength: number;
    language: string;
    artifactsEnabled: boolean | null;
    autoRenameEnabled: boolean | null;
    editorModelName: string | null;
    tabAutocomplete: boolean | null;
    tabAutocompleteEnabled: boolean | null;
  }) => {
    utils.userSettings.get.cancel();
    const oldSettings = settings ?? defaultSettings;

    utils.userSettings.get.setData(undefined, () => newSettings);

    mutate(newSettings, {
      onError: (_) => {
        utils.userSettings.get.setData(undefined, () => oldSettings);
      },
    });
  };

  useEffect(() => {
    if (
      userPlanDetails?.planName.toLowerCase() === 'free' &&
      settings &&
      (settings?.modelName !== DEFAULT_AI_MODELS[0] ||
        settings?.editorModelName !== DEFAULT_AI_MODELS[0])
    ) {
      const newSettings = {
        ...defaultSettings,
        ...settings,
        modelName: DEFAULT_AI_MODELS[0],
        editorModelName: DEFAULT_AI_MODELS[0],
      };

      updateSettings(newSettings);
    }
  }, [userPlanDetails, settings]);

  return {
    settings: settings ?? defaultSettings,
    isLoading,
    updateSettings,
    returnPage,
    setReturnPage,
  };
};
